/* eslint-disable */
import React, { useState, useEffect } from "react";
import {
  Table,
  Checkbox,
  Paper,
  Text,
  Group,
  Title,
  ScrollArea,
  createStyles,
  Button,
  Stack,
  Box,
} from "@mantine/core";

const useStyles = createStyles((theme) => ({
  container: {
    width: "100%",
    borderRadius: theme.radius.md,
    border: `1px solid ${theme.colors.gray[3]}`,
    overflow: "hidden",
  },
  headerRow: {
    background: theme.colors.gray[1],
    color: theme.colors.dark[9],
    fontWeight: 700,
  },
  cell: {
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    padding: `${theme.spacing.sm}px ${theme.spacing.md}px`,
    verticalAlign: "middle",
  },
  moduleCell: {
    paddingLeft: theme.spacing.lg,
  },
  rightHeader: {
    marginLeft: "auto",
    color: theme.colors.dark[6],
    fontSize: 14,
  },
  checkbox: {
    display: "flex",
    justifyContent: "center",
  },
  table: {
    minWidth: 900,
  },
  stickyHead: {
    position: "sticky",
    top: 0,
    zIndex: 2,
  },
}));

type PermFlags = {
  allowAll: boolean;
  read: boolean;
  write: boolean;
  create: boolean;
  delete: boolean;
  import: boolean;
  export: boolean;
};

type ModulePerm = {
  id: string;
  label: string;
  perms: PermFlags;
};

type Props = {
  roleName?: string;
  modules?: { id: string; label: string }[];
  onChange?: (payload: ModulePerm[]) => void;
};

const DEFAULT_MODULES = [
  "Employee",
  "Holidays",
  "Leaves",
  "Events",
  "Sales",
  "Training",
  "Reports",
  "Tickets",
  "Payroll",
];

export default function PermissionsTable({
  roleName = "Admin",
  modules,
  onChange,
}: Props) {
  const { classes } = useStyles();
  const moduleList =
    modules ?? DEFAULT_MODULES.map((m) => ({ id: m, label: m }));

  const initialState: ModulePerm[] = moduleList.map((m) => ({
    id: m.id,
    label: m.label,
    perms: {
      allowAll: false,
      read: false,
      write: false,
      create: false,
      delete: false,
      import: false,
      export: false,
    },
  }));

  const [rows, setRows] = useState<ModulePerm[]>(initialState);

  useEffect(() => {
    if (onChange) onChange(rows);
  }, [rows, onChange]);

  const updateFlag = (
    moduleId: string,
    key: keyof PermFlags,
    value: boolean
  ) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== moduleId) return r;
        const nextPerms = { ...r.perms, [key]: value };

        if (key === "allowAll") {
          Object.keys(nextPerms).forEach((k) => {
            // @ts-ignore
            nextPerms[k] = value;
          });
        } else {
          const { allowAll: _, ...indiv } = nextPerms;
          const allTrue = Object.values(indiv).every(Boolean);
          nextPerms.allowAll = allTrue;
        }

        return { ...r, perms: nextPerms };
      })
    );
  };

  const getPayload = () =>
    rows.map((r) => ({
      id: r.id,
      ...r.perms,
    }));

  const tableRows = rows.map((r) => (
    <tr key={r.id}>
      <td className={classes.cell + " " + classes.moduleCell}>
        <Text>{r.label}</Text>
      </td>

      <td className={classes.cell}>
        <div className={classes.checkbox}>
          <Checkbox
            checked={r.perms.allowAll}
            onChange={(e) =>
              updateFlag(r.id, "allowAll", e.currentTarget.checked)
            }
            color="orange"
            radius="xs"
            aria-label={`Allow all for ${r.label}`}
          />
        </div>
      </td>

      <td className={classes.cell}>
        <div className={classes.checkbox}>
          <Checkbox
            checked={r.perms.read}
            onChange={(e) => updateFlag(r.id, "read", e.currentTarget.checked)}
            color="orange"
            radius="xs"
            aria-label={`Read for ${r.label}`}
          />
        </div>
      </td>

      <td className={classes.cell}>
        <div className={classes.checkbox}>
          <Checkbox
            checked={r.perms.write}
            onChange={(e) => updateFlag(r.id, "write", e.currentTarget.checked)}
            color="orange"
            radius="xs"
            aria-label={`Write for ${r.label}`}
          />
        </div>
      </td>

      <td className={classes.cell}>
        <div className={classes.checkbox}>
          <Checkbox
            checked={r.perms.create}
            onChange={(e) =>
              updateFlag(r.id, "create", e.currentTarget.checked)
            }
            color="orange"
            radius="xs"
            aria-label={`Create for ${r.label}`}
          />
        </div>
      </td>

      <td className={classes.cell}>
        <div className={classes.checkbox}>
          <Checkbox
            checked={r.perms.delete}
            onChange={(e) =>
              updateFlag(r.id, "delete", e.currentTarget.checked)
            }
            color="orange"
            radius="xs"
            aria-label={`Delete for ${r.label}`}
          />
        </div>
      </td>

      <td className={classes.cell}>
        <div className={classes.checkbox}>
          <Checkbox
            checked={r.perms.import}
            onChange={(e) =>
              updateFlag(r.id, "import", e.currentTarget.checked)
            }
            color="orange"
            radius="xs"
            aria-label={`Import for ${r.label}`}
          />
        </div>
      </td>

      <td className={classes.cell}>
        <div className={classes.checkbox}>
          <Checkbox
            checked={r.perms.export}
            onChange={(e) =>
              updateFlag(r.id, "export", e.currentTarget.checked)
            }
            color="orange"
            radius="xs"
            aria-label={`Export for ${r.label}`}
          />
        </div>
      </td>
    </tr>
  ));

  return (
    <Paper className={classes.container} radius="md" p={0}>
      <Box style={{ display: "flex", alignItems: "center", padding: 16 }}>
        <Title order={4} style={{ margin: 0 }}>
          Permissions
        </Title>

        <Text className={classes.rightHeader}>
          Role Name : <strong style={{ marginLeft: 6 }}>{roleName}</strong>
        </Text>
      </Box>

      <ScrollArea>
        <Table className={classes.table} verticalSpacing="sm">
          <thead className={classes.stickyHead}>
            <tr className={classes.headerRow}>
              <th style={{ textAlign: "left", padding: "12px 16px" }}>
                Modules
              </th>
              <th style={{ textAlign: "center" }}>Allow All</th>
              <th style={{ textAlign: "center" }}>Read</th>
              <th style={{ textAlign: "center" }}>Write</th>
              <th style={{ textAlign: "center" }}>Create</th>
              <th style={{ textAlign: "center" }}>Delete</th>
              <th style={{ textAlign: "center" }}>Import</th>
              <th style={{ textAlign: "center" }}>Export</th>
            </tr>
          </thead>
          <tbody>{tableRows}</tbody>
        </Table>
      </ScrollArea>

      <Stack align="flex-end" spacing={8} p="sm">
        <Group>
          <Button
            variant="default"
            onClick={() => {
              setRows(initialState);
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={() => {
              const payload = getPayload();
              console.log("Saving permissions payload:", payload);

              if (onChange) onChange(rows);
            }}
            color="orange"
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}
